import axios from "axios";
import _ from "lodash";
import { convertToMinute, formatUtcLocalTime, formatUtcToStationLocalDateTime } from '../../helpers/formatData';
import constants from '../Modal/AttributionSearchBar/constants';
import { getStationsByIds } from '../../helpers/stationApi';

export const DEFAULT_MENTION_STATUS_IDS = '[1,2]';

function createVeritoneGraph(baseUrl) {
    return axios.create({
        baseURL: baseUrl,
        withCredentials: true
    });
}

async function apiFetchData(urlApi, query) {
    const result = await createVeritoneGraph(urlApi.graphql).post("", { query });
    return result.data;
}

export const ROOT_FOLDERS_QUERY = `
    {
        rootFolders(type: watchlist) {
            id
            name
            rootFolderTypeId
            ownerId
            childWatchlists{
                count
                records{
                  id
                  name
                }
            }
            childFolders {
                count
                records {
                    id
                    name
                }
            }
        }
    }
`;

export const WATCHLIST_QUERY = (offset, limit) => `
    {
        watchlists(limit: ${limit}, offset: ${offset}) {
            records {
                id
                name
                folders {
                    id
                }
            }
        }
    }
`;

export const FOLDER_DETAILS_QUERY_BY_ID = (id) => `
 {
        folder(id: "${id}") {
            id
            name
            parent {
                id
            }
            childWatchlists{
              count
              records {
                id
                name
              }
            }
            childFolders (limit: 200) {
              count
                records {
                  id
                  name
                }
            }
        }
    }
`;

export const FOLDER_DETAILS_QUERY_BY_ID_3_LEVEL = (id) => `
    {
        folder(id: "${id}") {
            id
            name
            maxDepth
            parent {
                id
            }
            subfolders {
                id
                name
                maxDepth
                parent {
                    id
                }
                subfolders {
                    id
                    name
                    maxDepth
                    parent {
                        id
                    }
                    subfolders {
                        id
                    }
                }
            }
        }
    }
`;

export const SEARCH_MENTIONS_BY_WATCHLISTS = (watchlists, stations, startDate, endDate) => `
    {
        searchMentions(search: {
            index: ["mine"],
            limit: 20,
            query: {
                operator: "and",
                conditions: [
                    {
                        operator: "terms",
                        field: "trackingUnitId",
                        values: [${watchlists}]
                    },
                    {
                        operator: "range"
                        field: "hitStartTime",
                        gte: "${startDate}",
                        lt: "${endDate}"
                    },
                    {
                        operator: "terms",
                        field: "mediaSourceId",
                        values: [${stations}]
                    }
                ]
            }
        }) {
            jsondata
        }
    }
`;

export const SEARCH_MENTIONS_AGGREGATE_BY_WATCHLISTS = (watchlists, stations = [], startDate, endDate) => `
    {
        searchMentions(search: {
            index: ["mine"],
            limit: 20,
            query: {
                operator: "and",
                conditions: [
                    {
                        operator: "terms",
                        field: "trackingUnitId",
                        values: [${watchlists}]
                    },
                    {
                        operator: "range"
                        field: "hitStartTime",
                        gte: "${startDate}",
                        lt: "${endDate}"
                    },
                    {
                        operator: "terms",
                        field: "mediaSourceId",
                        values: [${stations}]
                    }
                ]
            },
            aggregate: [
                {
                  name: "mediaSourceIds",
                  field: "mediaSourceId",
                  operator: "term",
                  limit: ${stations.length}
                },
                {
                   operator: "date",
                   field: "mentionDate",
                   format: "MM/dd"
                   interval: "day",
                   aggregate: [
                        {
                            operator: "count",
                            field: "id"
                        }
                    ]
                }
           ]
        }) {
            jsondata
        }
    }
`;

export const SEARCH_LIVEREAD_QUERY_GRAPHQL = (stations = [], valueSearch, startDate, endDate) => `
{
  searchMedia(
    search: {
      index: ["mine", "global"],
      limit: 100,
      offset: 0,
      query: {
        operator: "and",
        conditions: [
          {
            operator: "range"
            field: "absoluteStartTimeMs",
            gte: "${startDate}",
            lt: "${endDate}"
          },
          {
            operator: "terms",
            field: "mediaSourceId",
            values: [${stations}]
          },
          ${valueSearch}
        ]
      }
    }
  ) {
    jsondata
  }
} 
`;

export const SEARCH_LIVEREAD_AGGREGATE_QUERY_GRAPHQL = (stations, valueSearch, startDate, endDate) => `
{
    searchMedia(
      search: {
        index: ["mine", "global"],
        limit: 100,
        offset: 0,
        query: {
          operator: "and",
          conditions: [
            {
              operator: "range"
              field: "absoluteStartTimeMs",
              gte: "${startDate}",
              lt: "${endDate}"
            },
            {
              operator: "terms",
              field: "mediaSourceId",
              values: [${stations}]
            },
            ${valueSearch}
          ]
        },
        aggregate: [
          {
            name: "mediaSourceIds",
            field: "mediaSourceId",
            operator: "term",
            limit: ${stations.length}
          },
          {
            operator: "date",
            field: "absoluteStartTimeMs",
            format: "MM/dd",
            interval: "day",
            aggregate: [
                {
                    operator: "count",
                    field: "recordingId"
                }
            ]
          }
        ]
      }
    ) {
      jsondata
    }
  }
`;

export const SEARCH_VERIFIED_MENTIONS_QUERY_GRAPHQL = () => `
    {
        searchMentions(search: {
            index: ["mine"],
            limit: 500,
            offset: 0,
            query: {
                field: "mentionStatusId",
                operator: "terms",
                values: [7]
            }
        }) {
            jsondata
        }
    }
`;

export const SEARCH_WATCHLIST_BY_NAME = (name) => `
    {
        watchlists( limit: 100, name: "${name}" ) {
            records {
                id
                name
                folders {
                  folderPath {
                    id
                    name
                  }
                }
            }
        }
    }
`;

export const SEARCH_WATCHLIST_BY_ID = (id) => `
    {
        watchlists( limit: 10, id: "${id}" ) {
            records {
                id
                name
                folders {
                  folderPath {
                    id
                    name
                  }
                }
            }
        }
    }
`;

export const SEARCH_FOLDER_BY_NAME = (id, name) => `
    {
        folder (id : "${id}") {
        id    
        name
        childFolders(limit: 100, names: "${name}", nameMatch: contains) {
            count
            records {
                id
                name
                folderPath {
                    id
                    name
                }
            }
        }
    }
    }
`;

async function getMentionsByWatchlists(urlApi, watchlists, stations, startDate, endDate) {
    const data = await apiFetchData(urlApi, SEARCH_MENTIONS_BY_WATCHLISTS(watchlists, stations, startDate, endDate));
    const aggregateData = await getMentionsAggregateByWatchlists(urlApi, watchlists, stations, startDate, endDate)
    const results = _.get(data, 'data.searchMentions.jsondata.results', []);
    const mentions = _.slice(_.orderBy(results, ["hitStartTime"], ["desc"]), 0, constants.PREVIEW_COUNT);
    const stationIds = _.uniqBy(mentions, 'mediaSourceId').map(mention => _.get(mention, 'mediaSourceId'));

    const localStations = stationIds.length > 0 ? await getStationsByIds(urlApi, stationIds) : [];

    const listItems = mentions.map(mention => {
        const { id, hitStartTime, hitEndTime, programName, programId, mediaSourceId, mediaSourceName } = mention;
        let duration = 0;
        if (!!hitStartTime && !!hitEndTime) {
            const startTime = new Date(hitStartTime);
            const endTime = new Date(hitEndTime);
            duration = endTime.getTime() - startTime.getTime();
        }
        const stationDetails = localStations.find(station => parseInt(station.id) === parseInt(mediaSourceId));
        const stationDetailName = _.get(stationDetails, 'name');
        const stationTimezone = _.get(stationDetails, 'details.liveTimezone');

        return {
            advertiser: "N/A",
            id: id,
            hitStartTime,
            hitEndTime,
            lastAired: stationTimezone ? formatUtcToStationLocalDateTime(hitStartTime, stationTimezone) : formatUtcLocalTime(hitStartTime),
            adName: programName,
            adId: programId,
            stationId: mediaSourceId,
            mediaSourceName: mediaSourceName,
            stationName: stationDetailName ? stationDetailName : mediaSourceName,
            duration: duration === 0 ? "N/A" : convertToMinute(duration / 1000)
        };
    });

    return {
        listItems,
        aggregateData
    }
}

async function getMentionsAggregateByWatchlists(urlApi, watchlists, stations, startDate, endDate) {
  const aggregateResults =  await apiFetchData(urlApi, SEARCH_MENTIONS_AGGREGATE_BY_WATCHLISTS(watchlists, stations, startDate, endDate));
  const aggregateStationIds = _.uniq(
    _.get(aggregateResults, 'data.searchMentions.jsondata.aggregations.mediaSourceIds.buckets', [])
    .map(item => item.key)
  );
  const aggregateStationList = await getStationsByIds(urlApi, aggregateStationIds);
  const marketList = aggregateStationList.reduce((acc, item) => {
    const details = _.get(item, 'details', {});
    const { marketIds, homeMarketId } = details;
    const markets = marketIds || homeMarketId ? [homeMarketId] : [];
    acc = [...acc, ...markets];
    return acc;
  }, [])
  const marketIds = _.uniq(marketList);
  const aggregations = _.get(aggregateResults, 'data.searchMentions.jsondata.aggregations', {}) ;
  const adsPerDay =  _.get(aggregations, 'mentionDate.buckets', []) ;
  const { chartData, totalAds } = adsPerDay.reduce((acc, item) => {
    const { chartData } = acc;
    let { totalAds } = acc;
    const value =  _.get(item,'id.value', 0);
    const { key_as_string: time } = item;
    chartData.push({ time, value });
    acc.totalAds = totalAds + value;
    return acc;
  }, {
    chartData: [],
    totalAds: 0
  });
  const aggregateData = {
    totalMarkets: marketIds.length,
    totalStations: aggregateStationIds.length,
    totalAds,
    chartData
  };
  return aggregateData;
}

async function apiFetchAllFolders(urlApi, folders) {
    // get all folder from discovery
    if (folders.length === 0) {
        return [];
    } else {
        const fetchFolders = await Promise.all(folders.map(folder =>
            apiFetchData(urlApi, FOLDER_DETAILS_QUERY_BY_ID_3_LEVEL(folder.id))
        ));
        const foldersData = fetchFolders.map(fetchFolder => fetchFolder.data.folder);
        let allFolder = foldersData;

        //get folder child level 1
        let subFolderLevel1 = foldersData
            .map(folder => folder ? folder.subfolders : null);
        subFolderLevel1 = _.flatten(subFolderLevel1);
        subFolderLevel1 = subFolderLevel1.filter(folder => folder !== null && folder != []);
        //get folder child level 2
        let subFolderLevel2 = subFolderLevel1
            .map(folder => folder ? folder.subfolders : null);
        subFolderLevel2 = _.flatten(subFolderLevel2);
        subFolderLevel2 = subFolderLevel2.filter(folder => folder !== null && folder != []);
        allFolder = [...allFolder, ...subFolderLevel1, ...subFolderLevel2];
        const subFolders = subFolderLevel2.filter(folder => {
            let subTmp = _.get(folder, "subfolders", []);
            return subTmp.length > 0;
        });
        let subFolderFetchedData = await apiFetchAllFolders(urlApi, subFolders);
        //remove all duplicated value
        allFolder = _.unionBy(allFolder, subFolderFetchedData, "id");
        return allFolder;
    }

}

async function getMentionsStructuredData(urlApi, sdoTableName = 'sdo_station_pl_1_5_p_6_gaixjxp') {
    try {
        const res = await apiFetchData(urlApi, SEARCH_VERIFIED_MENTIONS_QUERY_GRAPHQL());
        const errors = _.get(res, 'errors');

        if (errors) {
            const errorMessage = _.get(errors, '[0].message', 'graphQL error');
            throw new Error(errorMessage);
        }

        const results = _.get(res, 'data.searchMentions.jsondata.results', []);

        return results.reduce((accum, result) => {
            const structuredData = _.get(result, ['cognitiveEngineResults', 'structuredData', sdoTableName, 'series', 0]);
            if (structuredData) {
                accum.push({
                    ...structuredData,
                    artistName: structuredData.artistName || ''
                });
            }
            return accum;
        }, []);

    } catch (error) {
        const errorMessage = get(error, 'Error', 'load structured sdos error');
        throw new Error(errorMessage);
    }
}

async function apiFetchLiveReadRecords(urlApi, stations, valueSearch, startDate, endDate) {
    return await apiFetchData(urlApi, SEARCH_LIVEREAD_QUERY_GRAPHQL(stations, valueSearch, startDate, endDate));
}

async function apiFetchLiveReadAggregateData(urlApi, stations, valueSearch, startDate, endDate) {
    return await apiFetchData(urlApi, SEARCH_LIVEREAD_AGGREGATE_QUERY_GRAPHQL(stations, valueSearch, startDate, endDate));
}

async function apiFetchFolderDetails(urlApi, folderId) {    
    const res = await apiFetchData(urlApi, FOLDER_DETAILS_QUERY_BY_ID(folderId));
    const folder = res.data.folder;    
    return folder;
}

async function apiSearchWatchlistByIdAndRequireFolders(urlApi, watchlistId, foldersState) {    
    const watchlistRes = await apiFetchData(urlApi, SEARCH_WATCHLIST_BY_ID(watchlistId));
    const watchlist = _.get(watchlistRes, 'data.watchlists.records[0]', null);
    const folderPath = _.get(watchlist, 'folders[0].folderPath', []);
    const expandFolderIds = folderPath.map(folder => folder.id);
    const missingDataFolderIds = expandFolderIds.filter(folderId => !_.get(foldersState, ['byId', folderId, 'loaded'], false));
    const missingDataFolderDetails = await apiFetchFoldersByChunk(urlApi, missingDataFolderIds);
    return {
        watchlist,
        folders: missingDataFolderDetails,
        expandFolderIds
    };
}

async function apiFetchFoldersByChunk(urlApi, folderIds){
    const numChunksToLoad = 3;
    const missingDataFoldersByChunk = _.chunk(folderIds, numChunksToLoad);
    
    let requireFoldersDetails = [];
    // get required folder details by chunk
    for (let idx = 0; idx < missingDataFoldersByChunk.length; idx++) {
        const element = missingDataFoldersByChunk[idx];
        
        const result = await Promise.all(element.map( async(folderId) =>
            await apiFetchFolderDetails(urlApi, folderId)
        ));
        requireFoldersDetails  = [...requireFoldersDetails, ...result];
    }
    return requireFoldersDetails;
}

 async function apiSearchFoldersAndWatchlistsByName(urlApi, foldersState, name){
    try{
        const rootId = _.get(foldersState, 'rootIds[0]', '');
        const folderRes = await apiFetchData(urlApi, SEARCH_FOLDER_BY_NAME(rootId, name));
        const watchlistRes = await apiFetchData(urlApi, SEARCH_WATCHLIST_BY_NAME(name));
    
        const foundFolders = _.get(folderRes, 'data.folder.childFolders.records', []);
        const watchlists = _.get(watchlistRes, 'data.watchlists.records', []);
        const requireFoldersObj = {};
        const expandFoldersObj = {};
        const missingDataFoldersObj = {};

        foundFolders.forEach(folderItem => {
            // get parent folders
            const { folderPath } = folderItem;
            folderPath && folderPath.forEach(folder => {
                const id = folder.id;
                requireFoldersObj[id] = true;
                if( id !== folderItem.id ){
                    expandFoldersObj[id] =  true;
                }
                if( !_.get(foldersState, ['byId', id, 'loaded'], false)){
                    missingDataFoldersObj[id] = true;
                }
            })
        });
        const foundWatchlists = watchlists.map(watchlist => {
            const folderPath = _.get(watchlist, 'folders[0].folderPath') || [];
            
            // get parent folders
            folderPath.forEach(folder => {
                const id = folder.id;
                requireFoldersObj[id] = true;
                expandFoldersObj[id] =  true;
                if( !_.get(foldersState, ['byId', id, 'loaded'], false)){
                    missingDataFoldersObj[id] = true;
                }
            })
            
            return {
                id: watchlist.id,
                name: watchlist.name,
                searchResult: true
            };
        });

        const requireFoldersDetails = await apiFetchFoldersByChunk(urlApi, Object.keys(missingDataFoldersObj));
        
        const requireFolders = Object.keys(requireFoldersObj);
        const expandFolders = Object.keys(expandFoldersObj);
        return {
            foundWatchlists,
            requireFoldersDetails,
            requireFolders,
            expandFolders
        };

    } catch(e) {
        throw new Error(e);
    }    
}

export default apiFetchData;
export {
    apiFetchAllFolders,
    apiFetchFolderDetails,
    getMentionsByWatchlists,
    apiFetchLiveReadRecords,
    apiFetchLiveReadAggregateData,
    getMentionsStructuredData,
    apiSearchFoldersAndWatchlistsByName,
    apiSearchWatchlistByIdAndRequireFolders
};
